import { getStudentData } from '../assets/data';
import { getLessonById } from '../utils/lessonsService';
import { createHomework, updateHomework, getHomeworkById, submitHomework, reviewHomework } from '../utils/homeworkService';
import { getStudentByInviteSlug } from '../utils/studentsService';

// Extracted from LessonWorkspace.js (Phase 3 of the platform-health plan:
// getting large files under a saner line count needs pulling logic into
// hooks, not just JSX into components). Owns the homework lifecycle from
// the teacher's/student's side inside the lesson editor: marking reviewed,
// and the "Send Homework" flow (student-id resolution across slug/UUID,
// creating-or-updating the Supabase row, the timestamp→UUID id rename
// across every per-homework state map, and the best-effort Telegram
// nudges). Moved byte-for-byte identical — this is dense and
// branch-heavy, not deeply order-sensitive like the save guard, but still
// touches several other tabs' state (whiteboards/containers/comments) so
// every setter it needs is passed in rather than re-derived.
export function useHomeworkSubmission({
  isStudent,
  studentId,
  lessonId,
  currentUser,
  homeworkDataCache,
  setHomeworkDataCache,
  homeworkContainers,
  setHomeworkContainers,
  editorContents,
  setEditorContents,
  allEditorsRef,
  setWhiteboards,
  setContainerComments,
  setSendHomeworkSubmittingId,
  setHomeworkSuccessToast,
}) {
  const handleMarkReviewed = async (homeworkId) => {
    const homeworkData = homeworkDataCache[homeworkId] || {};
    const hwId = String(homeworkId);
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(hwId);
    const idToUpdate = isUUID ? hwId : (await getHomeworkById(hwId).then(h => h?.id).catch(() => null)) || hwId;
    try {
      await reviewHomework(idToUpdate);
      setHomeworkDataCache(prev => ({
        ...prev,
        [homeworkId]: { ...homeworkData, status: 'reviewed' },
        ...(idToUpdate !== homeworkId ? { [idToUpdate]: { ...homeworkData, id: idToUpdate, status: 'reviewed' } } : {})
      }));
      window.dispatchEvent(new CustomEvent('homeworkUpdated', { detail: { homeworkId: idToUpdate, studentId: homeworkData.studentId } }));

      // Telegram nudge to the student. Same best-effort pattern as the
      // submit + assign notifications — silent if the chat isn't linked
      // or BOT_TOKEN is missing.
      try {
        const { forwardToTelegram } = await import('../utils/chatService');
        const sidForTg = homeworkData.studentId;
        console.log('[telegram-nudge:reviewed]', { sidForTg });
        const teacherName = (currentUser?.name || 'Your teacher').trim();
        const title = homeworkData.title || 'your homework';
        const text = `✅ Your teacher reviewed your homework: "${title}". Open NOVAS to see feedback.`;
        if (sidForTg) forwardToTelegram(sidForTg, text, 'teacher', teacherName).catch(err => console.warn('[telegram-nudge:reviewed] forward failed', err?.message));
      } catch (e) { console.warn('[telegram-nudge:reviewed] import failed', e?.message); }
    } catch (err) {
      console.error('Error marking homework as reviewed:', err);
      alert('Failed to mark as reviewed. Please try again.');
    }
  };

  const handleSendHomework = async (homeworkId) => {
    // Получаем текущие данные homework из кеша
    const homeworkData = homeworkDataCache[homeworkId] || {};

    // Build the list of containers we need to capture. Multiple fallbacks
    // because the homeworkId passed here can be either a timestamp (pre-load)
    // or a UUID (post-load), and the various maps key off whichever one
    // happened first. If we trust only homeworkContainers[homeworkId] we
    // sometimes get an empty array right after a timestamp→UUID rename, and
    // ship nothing to the teacher.
    let resolvedHomeworkId = String(homeworkId);
    let containers = homeworkContainers[resolvedHomeworkId] || [];
    if (containers.length === 0) {
      // Same-tab fallback ONLY: if this specific tab already has a Supabase
      // row under a different (UUID) id than the one we were called with,
      // check that id's containers too (handles the timestamp → UUID rename
      // race). This must NOT scan other homework tabs' containers — it
      // previously did, which let a brand-new, still-empty tab silently
      // "Send" a DIFFERENT tab's content instead of its own (or nothing).
      try {
        const existingForThisTab = await getHomeworkById(resolvedHomeworkId);
        if (existingForThisTab?.id && (homeworkContainers[existingForThisTab.id] || []).length > 0) {
          containers = homeworkContainers[existingForThisTab.id];
          resolvedHomeworkId = String(existingForThisTab.id);
        }
      } catch (_) {}
    }
    if (containers.length === 0) {
      // Last resort: derive from homeworkDataCache (hydrated from Supabase
      // on load) so a fresh submit still ships something.
      const cached = homeworkDataCache[homeworkId] || homeworkDataCache[resolvedHomeworkId] || {};
      const cachedContainers = cached.containerContents || cached.containers || [];
      if (Array.isArray(cachedContainers) && cachedContainers.length > 0) {
        containers = cachedContainers.map(cc => ({ id: cc.id, type: 'editor' }));
      }
    }

    // Read container contents preferring the live TipTap editor instance over
    // the editorContents state. The state update from onUpdate is async, so a
    // user who types and immediately clicks Submit can race the setEditorContents
    // commit and otherwise ship stale (or empty) HTML to the teacher. The
    // editor's id pattern is `homework-{hwId}-{containerId}` — different from
    // the editorContents key (which has a "container-" infix). Snapshot from
    // the live editor first; fall back to state.
    const liveHtmlForContainer = (containerId) => {
      try {
        // Try BOTH possible homeworkIds (URL value and resolved-from-map value).
        const candidates = [
          `homework-${homeworkId}-${containerId}`,
          `homework-${resolvedHomeworkId}-${containerId}`,
        ];
        for (const editorId of candidates) {
          const editor = allEditorsRef.current?.get?.(editorId);
          if (editor && typeof editor.getHTML === 'function') {
            return editor.getHTML();
          }
        }
      } catch (_) {}
      return null;
    };
    const containerContents = containers.map(container => {
      const live = liveHtmlForContainer(container.id);
      const staleA = editorContents[`homework-${homeworkId}-container-${container.id}`];
      const staleB = editorContents[`homework-${resolvedHomeworkId}-container-${container.id}`];
      return { id: container.id, content: live ?? staleA ?? staleB ?? '' };
    });

    setSendHomeworkSubmittingId(homeworkId);
    try {
    // Для студентов используем submitHomework
    if (isStudent) {
      try {
        console.log('Student submitting homework:', { homeworkId, homeworkData });

        // Проверяем, существует ли homework в Supabase
        const existingHomework = await getHomeworkById(homeworkId.toString());
        console.log('Existing homework from Supabase:', existingHomework);

        if (!existingHomework || !existingHomework.id) {
          console.error('Homework not found in Supabase:', homeworkId);
          alert('Homework not found. Please contact your teacher.');
          return;
        }

        // Используем UUID из Supabase
        const homeworkUuid = existingHomework.id;
        console.log('Using homework UUID:', homeworkUuid);

        // Snapshot the student's exercise answers. All interactive exercises
        // (Multiple Choice, Word Translation, Fill-in-the-Gaps, Matching,
        // True/False, etc.) keep their state inside ExerciseSyncContext, which
        // only persists to localStorage + a realtime broadcast — never to the
        // homework record. So if the teacher is offline at submit time, or
        // opens the homework later / from another device, every exercise reads
        // as untouched. Read the same localStorage blob the context writes to
        // and ship it inside `content` so the teacher's view can hydrate.
        const studentForKey = (typeof currentUser?.id === 'string' && currentUser.id) || studentId || 'student';
        let exerciseStatesSnapshot = null;
        try {
          const lsKey = `ex_sync_${lessonId}_${studentForKey}`;
          const raw = localStorage.getItem(lsKey);
          if (raw) exerciseStatesSnapshot = JSON.parse(raw);
        } catch (e) {
          console.warn('Could not snapshot exercise states:', e?.message);
        }

        const content = {
          containers: containerContents,
          content: homeworkData.content || '',
          title: homeworkData.title || existingHomework.title || 'Homework',
          description: homeworkData.description || existingHomework.description || '',
          // Per-exercise state keyed by exerciseId. The teacher's
          // LessonWorkspace seeds ExerciseSyncContext from this on load.
          exerciseStates: exerciseStatesSnapshot,
        };

        console.log('Submitting homework with content:', content);
        console.log('[RAW CAPTURE] containers[0].content:', JSON.stringify(content?.containers?.[0]?.content ?? null));
        await submitHomework(homeworkUuid, content);

        // Telegram nudge to the teacher (best-effort — silent if the teacher
        // isn't linked to the bot yet, or if BOT_TOKEN is missing).
        try {
          const { forwardToTelegram } = await import('../utils/chatService');
          const studentIdForTg = existingHomework.studentId || homeworkData.studentId;
          console.log('[telegram-nudge:submit]', { studentIdForTg, existingStudentId: existingHomework.studentId, homeworkStudentId: homeworkData.studentId });
          if (studentIdForTg) {
            const studentName = (currentUser?.name || homeworkData.studentName || 'Student').trim();
            const title = content.title || 'Homework';
            const text = `📥 ${studentName} submitted homework: "${title}". Open the lesson to review.`;
            forwardToTelegram(studentIdForTg, text, 'student', studentName).catch(err => console.warn('[telegram-nudge:submit] forward failed', err?.message));
          }
        } catch (e) { console.warn('[telegram-nudge:submit] import failed', e?.message); }

        // Обновляем кеш
        const updatedData = {
          ...homeworkData,
          id: homeworkUuid,
          status: 'submitted',
          submittedAt: new Date().toISOString(),
          containerContents: containerContents,
          containers: containerContents,
          exerciseStates: exerciseStatesSnapshot,
        };
        setHomeworkDataCache(prev => ({
          ...prev,
          [homeworkUuid]: updatedData
        }));

        // Отправляем событие об обновлении
        window.dispatchEvent(new CustomEvent('homeworkUpdated', {
          detail: { homeworkId: homeworkUuid, studentId: homeworkData.studentId }
        }));

        setHomeworkSuccessToast({ message: 'Homework submitted successfully!' });
        return;
      } catch (error) {
        console.error('Error submitting homework:', error);
        alert('Error submitting homework. Please try again.');
        return;
      }
    }

    // Определяем правильный studentId (slug)
    let finalStudentId = homeworkData.studentId || studentId;

    // Если studentId это UUID, находим соответствующий slug
    if (finalStudentId) {
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(finalStudentId);
      if (isUUID) {
        // Ищем студента по UUID в getStudentData()
        try {
          const studentData = getStudentData();
          const foundStudent = Object.entries(studentData).find(([slug, student]) =>
            student.studentId === finalStudentId || student.id === finalStudentId
          );
          if (foundStudent) {
            finalStudentId = foundStudent[0]; // Используем slug как ключ
          }
        } catch (e) {
          console.error('Error finding student slug:', e);
        }
      }
    }

    // Если studentId все еще не определен, пытаемся получить из урока
    if (!finalStudentId && lessonId && lessonId !== 'new') {
      try {
        const lesson = await getLessonById(lessonId);
        if (lesson && lesson.studentId) {
          let lessonStudentId = lesson.studentId;
          // Если это UUID, находим slug
          const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(lessonStudentId);
          if (isUUID) {
            try {
              const studentData = getStudentData();
              const foundStudent = Object.entries(studentData).find(([slug, student]) =>
                student.studentId === lessonStudentId || student.id === lessonStudentId
              );
              if (foundStudent) {
                lessonStudentId = foundStudent[0];
              }
            } catch (e) {
              console.error('Error finding student slug from lesson:', e);
            }
          }
          finalStudentId = lessonStudentId;
        }
      } catch (err) {
        console.error('Error loading lesson:', err);
      }
    }

    // Получаем UUID студента для Supabase (если finalStudentId это slug)
    let studentUuidForSupabase = null;
    if (finalStudentId && finalStudentId !== 'demo-student-1') {
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(finalStudentId);
      if (isUUID) {
        studentUuidForSupabase = finalStudentId;
      } else {
        // Это slug, нужно найти UUID
        try {
          const studentData = getStudentData();
          const foundStudent = Object.entries(studentData).find(([slug, student]) =>
            slug === finalStudentId || student.slug === finalStudentId
          );
          if (foundStudent && foundStudent[1]) {
            studentUuidForSupabase = foundStudent[1].studentId || foundStudent[1].id;
          } else {
            // Пробуем загрузить из Supabase
            const studentFromSupabase = await getStudentByInviteSlug(finalStudentId);
            if (studentFromSupabase) {
              studentUuidForSupabase = studentFromSupabase.id;
            }
          }
        } catch (e) {
          console.error('Error finding student UUID:', e);
        }
      }
    }

    // Для скопированного урока: если студент не определён, берём student_id из урока в Supabase
    const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!studentUuidForSupabase && lessonId && lessonId !== 'new' && uuidRe.test(String(lessonId))) {
      try {
        const lesson = await getLessonById(lessonId);
        if (lesson?.studentId && uuidRe.test(String(lesson.studentId))) {
          studentUuidForSupabase = lesson.studentId;
          if (!finalStudentId) finalStudentId = lesson.studentId;
        }
      } catch (e) {
        console.error('Error loading lesson for student_id:', e);
      }
    }

    // Обновляем данные homework
    const updatedHomeworkData = {
      ...homeworkData,
      id: homeworkId,
      status: 'assigned',
      assignedAt: new Date().toISOString(),
      containerContents: containerContents,
      containers: containerContents, // Для совместимости
      lessonId: lessonId || 'new',
      studentId: finalStudentId || homeworkData.studentId || 'demo-student-1',
      title: homeworkData.title || 'Homework',
      description: homeworkData.description || '',
      content: homeworkData.content || '',
      dueDate: homeworkData.dueDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
    };

    // Сохраняем в Supabase
    const studentIdForSupabase = studentUuidForSupabase || finalStudentId || updatedHomeworkData.studentId;
    console.log('Attempting to save homework to Supabase:', { homeworkId, studentIdForSupabase, studentUuidForSupabase, finalStudentId });

    try {
      // Используем UUID для Supabase; для скопированного урока всегда берём lessonId из URL (текущий урок)
      const effectiveLessonId = (lessonId && lessonId !== 'new' && uuidRe.test(String(lessonId))) ? lessonId : (updatedHomeworkData.lessonId || null);
      const homeworkForSupabase = {
        ...updatedHomeworkData,
        lessonId: effectiveLessonId || updatedHomeworkData.lessonId,
        studentId: studentIdForSupabase,
        containers: containerContents // Для Supabase используем containers, а не containerContents
      };

      // Проверяем, существует ли homework в Supabase и принадлежит ли он текущему уроку
      const existingHomework = await getHomeworkById(homeworkId.toString());
      const belongsToCurrentLesson = existingHomework?.lessonId != null && String(existingHomework.lessonId) === String(lessonId);

      if (existingHomework && existingHomework.id && belongsToCurrentLesson) {
        // A row with status 'pending' only ever exists here because
        // eagerlyCreateHomeworkRow silently created it in the background —
        // the student has never actually been told about this homework yet,
        // so this click is really a first SEND, not an update, even though
        // it goes through this "existing row" branch. Capture that before
        // the status write below overwrites it.
        const wasAlreadySent = !!existingHomework.status && existingHomework.status !== 'pending';

        // Обновляем только если это ДЗ именно этого урока (не скопированного)
        console.log('Updating existing homework in Supabase:', homeworkId);
        await updateHomework(homeworkId.toString(), {
          status: 'assigned',
          assignedAt: updatedHomeworkData.assignedAt,
          dueDate: updatedHomeworkData.dueDate,
          title: updatedHomeworkData.title,
          description: updatedHomeworkData.description,
          containers: containerContents,
          content: {
            title: updatedHomeworkData.title,
            description: updatedHomeworkData.description,
            content: updatedHomeworkData.content,
            containers: containerContents,
            bannerImage: homeworkData.bannerImage || null
          }
        });
        console.log('Homework updated successfully in Supabase');

        const finalHomeworkId = existingHomework.id;
        const updatedData = {
          ...updatedHomeworkData,
          id: finalHomeworkId
        };
        setHomeworkDataCache(prev => ({
          ...prev,
          [finalHomeworkId]: updatedData
        }));
        window.dispatchEvent(new CustomEvent('homeworkUpdated', { detail: { homeworkId: finalHomeworkId, studentId: updatedHomeworkData.studentId } }));
        setHomeworkSuccessToast({
          message: wasAlreadySent
            ? 'Update sent! The student will be notified.'
            : 'Homework sent successfully! It will appear in the student\'s profile.'
        });

        // Telegram nudge to the student — same best-effort pattern as the
        // create-new-homework branch below, just with wording that matches
        // whether this is the first time they're hearing about it or the
        // teacher changed something after already sending it.
        try {
          const { forwardToTelegram } = await import('../utils/chatService');
          const sidForTg = updatedHomeworkData.studentId;
          console.log('[telegram-nudge:assign-update]', { sidForTg, wasAlreadySent });
          const teacherName = (currentUser?.name || 'Your teacher').trim();
          const title = updatedHomeworkData.title || 'Homework';
          const due = updatedHomeworkData.dueDate
            ? new Date(updatedHomeworkData.dueDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
            : null;
          const text = wasAlreadySent
            ? `🔄 Your teacher updated homework: "${title}". Open NOVAS to see the changes.`
            : (due
              ? `📝 New homework: "${title}". Due ${due}. Open NOVAS to start.`
              : `📝 New homework: "${title}". Open NOVAS to start.`);
          if (sidForTg) forwardToTelegram(sidForTg, text, 'teacher', teacherName).catch(err => console.warn('[telegram-nudge:assign-update] forward failed', err?.message));
        } catch (e) { console.warn('[telegram-nudge:assign-update] import failed', e?.message); }
      } else {
        // Создаем новое homework (новый урок, скопированный урок или ДЗ от другого урока)
        // Не передаём id — иначе при скопированном уроке id уже есть в БД и будет 23505 duplicate key
        if (!belongsToCurrentLesson && existingHomework?.id) {
          console.log('Homework tab is from another lesson (e.g. copied), creating new row for current lesson:', lessonId);
        }
        const createPayload = { ...homeworkForSupabase };
        delete createPayload.id;
        console.log('Creating new homework in Supabase (no id)');
        const createdHomework = await createHomework(createPayload);
        console.log('Homework created successfully in Supabase:', createdHomework);

        if (createdHomework && createdHomework.id) {
          const updatedData = {
            ...updatedHomeworkData,
            id: createdHomework.id
          };
          setHomeworkDataCache(prev => ({
            ...prev,
            [createdHomework.id]: updatedData
          }));

          // Rename this homework's timestamp id → fresh UUID across all the
          // per-homework maps. Without this the tab keeps a timestamp id
          // forever, the second homework tab in the same lesson can't be
          // disambiguated in cache lookups, and the render heuristic falls
          // back to the wrong UUID — leaking another homework's content
          // into this tab. Same rename code as in loadHomeworkData.
          const newHwUuid = String(createdHomework.id);
          const oldHwId = String(homeworkId);
          if (oldHwId !== newHwUuid) {
            setWhiteboards(prev => prev.map(wb =>
              String(wb.id) === oldHwId && wb.type === 'homework'
                ? { ...wb, id: newHwUuid }
                : wb
            ));
            setHomeworkContainers(prev => {
              if (!prev[oldHwId]) return prev;
              const next = { ...prev };
              next[newHwUuid] = next[oldHwId];
              delete next[oldHwId];
              return next;
            });
            setEditorContents(prev => {
              const next = { ...prev };
              Object.keys(prev).forEach(k => {
                if (k.startsWith(`homework-${oldHwId}-container-`)) {
                  const nk = k.replace(`homework-${oldHwId}-container-`, `homework-${newHwUuid}-container-`);
                  next[nk] = prev[k];
                  delete next[k];
                }
              });
              return next;
            });
            setContainerComments(prev => {
              const next = { ...prev };
              Object.keys(prev).forEach(k => {
                if (k.startsWith(`homework-${oldHwId}-container-`)) {
                  const nk = k.replace(`homework-${oldHwId}-container-`, `homework-${newHwUuid}-container-`);
                  next[nk] = prev[k];
                  delete next[k];
                }
              });
              return next;
            });
          }

          window.dispatchEvent(new CustomEvent('homeworkUpdated', { detail: { homeworkId: createdHomework.id, studentId: updatedHomeworkData.studentId } }));
          setHomeworkSuccessToast({ message: 'Homework sent successfully! It will appear in the student\'s profile.' });

          // Telegram nudge to the student. Best-effort: silent if the
          // student hasn't linked their account to the bot from the chat
          // panel yet, or if BOT_TOKEN is missing.
          try {
            const { forwardToTelegram } = await import('../utils/chatService');
            const sidForTg = updatedHomeworkData.studentId;
            console.log('[telegram-nudge:assign-create]', { sidForTg, studentUuidForSupabase, finalStudentId });
            const teacherName = (currentUser?.name || 'Your teacher').trim();
            const title = updatedHomeworkData.title || 'Homework';
            const due = updatedHomeworkData.dueDate
              ? new Date(updatedHomeworkData.dueDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
              : null;
            const text = due
              ? `📝 New homework: "${title}". Due ${due}. Open NOVAS to start.`
              : `📝 New homework: "${title}". Open NOVAS to start.`;
            if (sidForTg) forwardToTelegram(sidForTg, text, 'teacher', teacherName).catch(err => console.warn('[telegram-nudge:assign-create] forward failed', err?.message));
          } catch (e) { console.warn('[telegram-nudge:assign-create] import failed', e?.message); }
        }
      }
    } catch (error) {
      console.error('Error saving homework to Supabase:', String(error?.message ?? error?.code ?? error));
      // Пробуем создать новое, если обновление не удалось
      // Определяем homeworkForSupabase вне try-catch, чтобы она была доступна в catch
      const homeworkForSupabase = {
        ...updatedHomeworkData,
        lessonId: (lessonId && lessonId !== 'new' && uuidRe.test(String(lessonId))) ? lessonId : updatedHomeworkData.lessonId,
        studentId: studentIdForSupabase,
        containers: containerContents
      };
      // Всегда создаём новую запись без id, иначе возможен 23505 duplicate key (id уже в БД)
      delete homeworkForSupabase.id;

      try {
        const createdHomework = await createHomework(homeworkForSupabase);
        console.log('Homework created successfully in Supabase (fallback):', createdHomework);

        // Обновляем кеш с новым UUID из Supabase
        if (createdHomework && createdHomework.id) {
          const updatedData = {
            ...updatedHomeworkData,
            id: createdHomework.id
          };
          setHomeworkDataCache(prev => ({
            ...prev,
            [createdHomework.id]: updatedData
          }));

          // Same timestamp → UUID rename as the primary create path (see
          // its comment for why). Without this, the second homework tab
          // in a lesson keeps a timestamp id forever and gets confused
          // with the first one's cache UUID.
          const newHwUuid = String(createdHomework.id);
          const oldHwId = String(homeworkId);
          if (oldHwId !== newHwUuid) {
            setWhiteboards(prev => prev.map(wb =>
              String(wb.id) === oldHwId && wb.type === 'homework'
                ? { ...wb, id: newHwUuid }
                : wb
            ));
            setHomeworkContainers(prev => {
              if (!prev[oldHwId]) return prev;
              const next = { ...prev };
              next[newHwUuid] = next[oldHwId];
              delete next[oldHwId];
              return next;
            });
            setEditorContents(prev => {
              const next = { ...prev };
              Object.keys(prev).forEach(k => {
                if (k.startsWith(`homework-${oldHwId}-container-`)) {
                  const nk = k.replace(`homework-${oldHwId}-container-`, `homework-${newHwUuid}-container-`);
                  next[nk] = prev[k];
                  delete next[k];
                }
              });
              return next;
            });
            setContainerComments(prev => {
              const next = { ...prev };
              Object.keys(prev).forEach(k => {
                if (k.startsWith(`homework-${oldHwId}-container-`)) {
                  const nk = k.replace(`homework-${oldHwId}-container-`, `homework-${newHwUuid}-container-`);
                  next[nk] = prev[k];
                  delete next[k];
                }
              });
              return next;
            });
          }

          window.dispatchEvent(new CustomEvent('homeworkUpdated', { detail: { homeworkId: createdHomework.id, studentId: updatedHomeworkData.studentId } }));
          setHomeworkSuccessToast({ message: 'Homework sent successfully! It will appear in the student\'s profile.' });

          // Telegram nudge to the student (fallback create path).
          try {
            const { forwardToTelegram } = await import('../utils/chatService');
            const sidForTg = updatedHomeworkData.studentId;
            console.log('[telegram-nudge:assign-fallback]', { sidForTg });
            const teacherName = (currentUser?.name || 'Your teacher').trim();
            const title = updatedHomeworkData.title || 'Homework';
            const due = updatedHomeworkData.dueDate
              ? new Date(updatedHomeworkData.dueDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
              : null;
            const text = due
              ? `📝 New homework: "${title}". Due ${due}. Open NOVAS to start.`
              : `📝 New homework: "${title}". Open NOVAS to start.`;
            if (sidForTg) forwardToTelegram(sidForTg, text, 'teacher', teacherName).catch(err => console.warn('[telegram-nudge:assign-fallback] forward failed', err?.message));
          } catch (e) { console.warn('[telegram-nudge:assign-fallback] import failed', e?.message); }
        }
      } catch (createError) {
        console.error('Error creating homework in Supabase:', String(createError?.message ?? createError?.code ?? createError));
        // Если ошибка из-за UUID, пробуем создать без id
        if ((createError.code === '22P02' || createError?.message?.includes('uuid')) && homeworkForSupabase.id) {
          try {
            const homeworkWithoutId = { ...homeworkForSupabase };
            delete homeworkWithoutId.id;
            const createdHomework = await createHomework(homeworkWithoutId);
            if (createdHomework && createdHomework.id) {
              const updatedData = {
                ...updatedHomeworkData,
                id: createdHomework.id
              };
              setHomeworkDataCache(prev => ({
                ...prev,
                [createdHomework.id]: updatedData
              }));
              window.dispatchEvent(new CustomEvent('homeworkUpdated', { detail: { homeworkId: createdHomework.id, studentId: updatedHomeworkData.studentId } }));
              setHomeworkSuccessToast({ message: 'Homework sent successfully! It will appear in the student\'s profile.' });
              console.log('Homework created successfully in Supabase (without id):', createdHomework);
            }
          } catch (retryError) {
            console.error('Error creating homework (retry):', String(retryError?.message ?? retryError));
            alert(retryError?.message || 'Не удалось отправить домашнее задание. Проверьте консоль.');
          }
        } else {
          alert(createError?.message || 'Не удалось отправить домашнее задание. Проверьте консоль.');
        }
      }
    }

    } finally {
      setSendHomeworkSubmittingId(null);
    }
  };

  return { handleMarkReviewed, handleSendHomework };
}
